import { httpsCallable, functions } from '@/lib/functions';
import type { Trip } from '@/services/trips';
import type { TripBudgetSnapshot } from '@/types/budget';
import { formatCurrency } from '@/utils/currency';

type CompletedTripDossierRequest = {
  action: 'request';
  tripId: string;
  channel: 'pdf' | 'csv' | 'api' | 'email';
};

type CompletedTripDossierStatus = {
  action: 'status';
  jobId: string;
};

type CompletedTripDossierResponse = {
  jobId: string;
  status: 'pending' | 'ready' | 'queued' | 'failed';
  downloadUrl?: string;
  textDownloadUrl?: string;
  visualDownloadUrl?: string;
  message?: string;
};

const callable = httpsCallable(functions, 'completedTripDossier');

export const requestDossierExport = async (
  tripId: string,
  channel: 'pdf' | 'csv' | 'api' | 'email',
): Promise<CompletedTripDossierResponse> => {
  const response = await callable({
    action: 'request',
    tripId,
    channel,
  } as CompletedTripDossierRequest);
  return response.data as CompletedTripDossierResponse;
};

export const fetchDossierStatus = async (jobId: string): Promise<CompletedTripDossierResponse> => {
  const response = await callable({
    action: 'status',
    jobId,
  } as CompletedTripDossierStatus);
  return response.data as CompletedTripDossierResponse;
};

export type RecapTravelStyle =
  | 'Cultural Globetrotter'
  | 'Food Trailblazer'
  | 'Budget Explorer'
  | 'Luxury Escapist'
  | 'Adventure Seeker';

export type TripRecapMetrics = {
  tripTitle: string;
  destinationLabel: string;
  durationDays: number;
  stopCount: number;
  travelerCount: number;
  totalSpent: number | null;
  budgetAmount: number | null;
  budgetPercent: number | null;
  placesVisitedCount: number;
  memoriesCount: number;
  memorySnippet?: string;
  vibeCheck: string;
  style: RecapTravelStyle;
  baseCurrency: string;
};

type RecapMetricsOptions = {
  placesVisitedCount?: number;
  memoryCount?: number;
  memorySnippet?: string;
};

const inferRecapTravelStyle = (
  totalSpent: number | null,
  budgetRatio: number | null,
  durationDays: number,
  memoryCount: number,
): RecapTravelStyle => {
  if (budgetRatio !== null && budgetRatio <= 0.6) {
    return 'Budget Explorer';
  }
  if (budgetRatio !== null && budgetRatio >= 1.25) {
    return 'Luxury Escapist';
  }
  if (memoryCount >= Math.max(3, Math.round(durationDays / 4))) {
    return 'Cultural Globetrotter';
  }
  if (totalSpent !== null && totalSpent > 0 && durationDays <= 4) {
    return 'Food Trailblazer';
  }
  return 'Adventure Seeker';
};

export const buildTripRecapMetrics = (
  trip: Trip,
  budgetSnapshot?: TripBudgetSnapshot,
  options?: RecapMetricsOptions,
): TripRecapMetrics => {
  const baseCurrency = trip.baseCurrency || 'USD';
  const totalSpent = budgetSnapshot
    ? (budgetSnapshot.expensesConverted ?? 0) + (budgetSnapshot.bookingsConverted ?? 0)
    : null;
  const durationDays = Math.max(
    1,
    Math.round((trip.endDate.getTime() - trip.startDate.getTime()) / (1000 * 60 * 60 * 24)),
  );
  const stopCount = (trip.destinations?.length ?? (trip.destination ? 1 : 0)) || 1;
  const travelerCount = trip.collaborators?.length ?? 0;
  const memoriesCount = options?.memoryCount ?? 0;
  const budgetAmount = typeof trip.budget === 'number' ? trip.budget : null;
  const budgetPercent =
    budgetAmount && totalSpent !== null && budgetAmount > 0 ? Math.round((totalSpent / budgetAmount) * 100) : null;
  const budgetRatio =
    budgetAmount && totalSpent !== null && budgetAmount > 0 ? totalSpent / budgetAmount : null;

  const style = inferRecapTravelStyle(totalSpent, budgetRatio, durationDays, memoriesCount);
  const destinationLabel = trip.destination || trip.destinations?.[0]?.city || trip.name;

  return {
    tripTitle: trip.name,
    destinationLabel,
    durationDays,
    stopCount,
    travelerCount: travelerCount || 1,
    totalSpent,
    budgetAmount,
    budgetPercent,
    placesVisitedCount: options?.placesVisitedCount ?? stopCount,
    memoriesCount,
    memorySnippet: options?.memorySnippet,
    vibeCheck: totalSpent && totalSpent > 0 ? 'Memories > receipts' : 'Trip moments still syncing',
    style,
    baseCurrency,
  };
};

export const buildRecapShareText = (
  trip: Trip,
  budgetSnapshot?: TripBudgetSnapshot,
  memoriesSnippet?: string,
  templateShareUrl?: string,
): string => {
  const metrics = buildTripRecapMetrics(trip, budgetSnapshot, { memorySnippet: memoriesSnippet });
  const header = `Post-trip recap: ${metrics.tripTitle}`;
  const spendLine = metrics.totalSpent === null
    ? '• Spend: syncing latest totals'
    : metrics.budgetAmount
      ? `• Spend: ${formatCurrency(metrics.totalSpent, metrics.baseCurrency)} (budget ${formatCurrency(metrics.budgetAmount, metrics.baseCurrency)})`
      : `• Spend: ${formatCurrency(metrics.totalSpent, metrics.baseCurrency)}`;
  const collabLine = `• ${metrics.durationDays} days, ${metrics.stopCount} ${
    metrics.stopCount === 1 ? 'stop' : 'stops'
  }, ${metrics.travelerCount} traveler${metrics.travelerCount === 1 ? '' : 's'}`;
  const memoryLine = metrics.memorySnippet ? `• Highlight: "${metrics.memorySnippet}"` : undefined;
  const styleLine = `• Travel style: ${metrics.style}`;
  const vibeLine = `• Vibe check: ${metrics.vibeCheck}.`;

  return [
    header,
    spendLine,
    collabLine,
    memoryLine,
    styleLine,
    vibeLine,
    '',
    templateShareUrl ? `Use this template: ${templateShareUrl}` : undefined,
    'Plan a trip like this with Tripvio.',
  ]
    .filter(Boolean)
    .join('\n');
};






